import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Sparkle, ArrowRight, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

const SPECS = ["Vedic", "Nadi", "KP", "Tarot", "Numerology", "Palmistry", "Vastu", "Lal Kitab", "Western", "Face Reading"];
const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Bengali", "Marathi", "Gujarati", "Punjabi", "Kannada", "Malayalam", "Sanskrit", "Urdu"];

const handleSignInClick = (returnTo) => (e) => {
  e.preventDefault();
  const redirect = window.location.origin + returnTo;
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  startGoogleSignIn(redirect);
};

export default function AstrologerRegisterPage() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    specializations: [],
    languages: ["English"],
    experience_years: 5,
    rate_per_min: 20,
    bio: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.specializations.length === 0) { toast.error("Pick at least one specialization"); return; }
    if (form.languages.length === 0) { toast.error("Pick at least one language"); return; }
    if (!form.bio.trim() || form.bio.trim().length < 30) { toast.error("Please share a short bio (30+ chars)"); return; }
    setLoading(true);
    try {
      await api.post("/astrologer/apply", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application. Please try again.");
    } finally { setLoading(false); }
  };

  // ─────────── NOT-SIGNED-IN GATE ───────────
  if (!authLoading && !user) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-center" data-testid="astro-register-signin-gate">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs font-bold tracking-wider">
            <ShieldCheck size={14} weight="duotone" /> SIGN-IN REQUIRED
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-5 font-light leading-tight">
            Become an <span className="text-gold italic">AstroKuber</span> astrologer
          </h1>
          <p className="text-muted mt-5 font-light max-w-2xl mx-auto leading-relaxed">
            Please sign in first — we use it to track your application and to reach out to you once you're shortlisted. It only takes a moment.
          </p>
          <a
            href="#"
            onClick={handleSignInClick("/astrologer/register")}
            data-testid="astro-register-signin-btn"
            className="inline-flex items-center gap-2 mt-9 px-7 py-3.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-sm tracking-wider hover:shadow-xl hover:scale-105 transition-all"
          >
            <Sparkle size={16} weight="fill" /> Sign in to continue
            <ArrowRight size={14} weight="bold" />
          </a>
          <p className="text-xs text-muted mt-6 font-light">
            Already onboarded? <Link to="/astrologer/login" className="text-gold underline">Log in to your dashboard</Link>
          </p>
        </div>
        <Footer />
      </PageShell>
    );
  }

  // ─────────── SUCCESS STATE ───────────
  if (submitted) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-center" data-testid="astro-register-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center">
            <CheckCircle size={42} weight="duotone" className="text-emerald-600" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-6 font-light leading-tight text-[#3F2D1B]">
            Thank you, <span className="text-gold italic">{form.name.split(" ")[0] || user?.name?.split(" ")[0]}</span>!
          </h1>
          <p className="text-[#3F2D1B] mt-5 font-light text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            <span className="font-bold">We'll review your astrologer registration application</span> shortly.
            <br />
            If your profile is shortlisted, we'll contact you over <span className="font-bold">{form.email}</span> with the next steps.
          </p>
          <div className="mt-10 rounded-2xl p-6 bg-amber-50 border border-amber-200 max-w-xl mx-auto text-left text-sm font-light text-[#3F2D1B] space-y-1.5">
            <div><span className="text-muted">Name:</span> {form.name}</div>
            <div><span className="text-muted">Specializations:</span> {form.specializations.join(", ")}</div>
            <div><span className="text-muted">Languages:</span> {form.languages.join(", ")}</div>
            <div><span className="text-muted">Experience:</span> {form.experience_years} year(s)</div>
            <div><span className="text-muted">Rate:</span> ₹{form.rate_per_min} / min</div>
          </div>
          <Link
            to="/"
            data-testid="astro-register-success-home"
            className="inline-flex items-center gap-2 mt-10 px-7 py-3 rounded-full bg-[#3F2D1B] text-amber-50 font-bold text-sm tracking-wider hover:shadow-lg transition-all"
          >
            Back to home
          </Link>
        </div>
        <Footer />
      </PageShell>
    );
  }

  // ─────────── APPLICATION FORM ───────────
  return (
    <PageShell>
      <Header />
      <div className="max-w-2xl mx-auto px-6 pt-12 pb-24" data-testid="astrologer-register-form">
        <p className="label-eyebrow text-[#7C2D12]">Apply to onboard</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light text-[#3F2D1B] leading-tight">
          Become an astrologer
        </h1>
        <p className="text-muted mt-4 font-light max-w-xl leading-relaxed">
          Share your craft with thousands of seekers. Fill the form below — we'll review your application and reach out by email if your profile is shortlisted.
        </p>

        <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className="label-eyebrow">Full name</label>
            <input required value={form.name} onChange={(e) => upd("name", e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none" data-testid="reg-name" />
          </div>
          <div>
            <label className="label-eyebrow">Email</label>
            <input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none" data-testid="reg-email" />
          </div>
          <div>
            <label className="label-eyebrow">Phone (optional)</label>
            <input value={form.phone} onChange={(e) => upd("phone", e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none" placeholder="+91…" data-testid="reg-phone" />
          </div>

          <div className="sm:col-span-2">
            <label className="label-eyebrow">Specializations <span className="text-muted normal-case">· pick all that apply</span></label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SPECS.map((s) => (
                <button type="button" key={s} onClick={() => toggleArr("specializations", s)}
                  data-testid={`spec-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.specializations.includes(s) ? "bg-[#FFB800] text-[#1A1410] border-[#FFB800]" : "border-soft text-muted hover:border-gold"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="label-eyebrow">Languages <span className="text-muted normal-case">· pick all you can consult in</span></label>
            <div className="flex flex-wrap gap-2 mt-2">
              {LANGS.map((l) => (
                <button type="button" key={l} onClick={() => toggleArr("languages", l)}
                  data-testid={`lang-${l}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.languages.includes(l) ? "bg-[#FFB800] text-[#1A1410] border-[#FFB800]" : "border-soft text-muted hover:border-gold"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow">Years of experience</label>
            <input type="number" min={0} value={form.experience_years} onChange={(e) => upd("experience_years", Number(e.target.value))}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none" data-testid="reg-exp" />
          </div>
          <div>
            <label className="label-eyebrow">Rate per minute (₹)</label>
            <input type="number" min={1} value={form.rate_per_min} onChange={(e) => upd("rate_per_min", Number(e.target.value))}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none" data-testid="reg-rate" />
          </div>

          <div className="sm:col-span-2">
            <label className="label-eyebrow">Short bio</label>
            <textarea required rows={4} value={form.bio} onChange={(e) => upd("bio", e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-soft rounded-md focus:border-gold outline-none"
              placeholder="Tell seekers about your practice — your lineage, techniques, signature reading style..."
              data-testid="reg-bio" />
            <p className="text-[11px] text-muted mt-1.5 font-light">{form.bio.trim().length} / 30+ chars</p>
          </div>

          <button disabled={loading} type="submit"
            className="sm:col-span-2 btn-primary rounded-full py-3.5 font-medium disabled:opacity-50"
            data-testid="reg-submit">
            {loading ? "Submitting…" : "Submit application"}
          </button>
          <p className="sm:col-span-2 text-xs text-muted text-center font-light leading-relaxed">
            By submitting, you agree to be evaluated by our editorial team. We'll email you at <b>{form.email || "(your email)"}</b> if your profile is shortlisted.
          </p>
        </form>
      </div>
      <Footer />
    </PageShell>
  );
}
